// Opportunities page — admin view
// Groups cards by status: WAITING_APPROVAL and APPROVED
// Admin can: add new, edit inline, approve pending

import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useOpportunities,
  useApproveOpportunity,
  useUpsertOpportunity,
  useUpdateOpportunity,
  useDeleteOpportunity,
  OpportunityStatus,
} from '@startus/opportunities';
import type {
  OpportunityQueryParams,
  Opportunity,
  UpsertOpportunityPayload,
} from '@startus/opportunities';
import { InlineField } from '@startus/applications-ui';
import {
  useOpportunityTypes,
  useFieldOptions,
  useEligibility,
  useRegions,
  useDebouncedSearch,
  getErrorMessage,
  captureException,
} from '@startus/lib';
import type { FieldOption, SelectOption } from '@startus/lib';
import { EmptyState, Pagination, MultiSelect } from '@startus/applications-ui';
import type { MultiSelectOption } from '@startus/applications-ui';
import { CountryMultiSelect } from '../../components/CountryMultiSelect';
import opportunitiesEmptyImage from '@startus/ui/src/assets/icons/opportunities-empty.svg';
import { ConfirmModal } from '@startus/ui';

const PAGE_SIZE = 20;

const STATUS_COLORS: Record<OpportunityStatus, string> = {
  [OpportunityStatus.WAITING_APPROVAL]: '#9ca3af',
  [OpportunityStatus.APPROVED]: '#22c55e',
};

// Parse ISO date to DD.MM.YYYY without timezone shift
function formatDate(iso: string | null, fallback: string): string {
  if (!iso) return fallback;
  const [datePart] = iso.split('T');
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return fallback;
  return `${day}.${month}.${year}`;
}

// Extract YYYY-MM-DD for date inputs
function toInputDate(iso: string | null): string {
  if (!iso) return '';
  return iso.split('T')[0];
}

// Convert YYYY-MM-DD to ISO at noon to avoid timezone day shift
function toISODate(dateStr: string): string {
  if (!dateStr) return '';
  return `${dateStr}T12:00:00.000Z`;
}

// ── Draft type for new unsaved opportunities ──────────────────────────────────

interface OpportunityDraft {
  __draft: true;
  id: string;
  name: string;
  description: string;
  link: string;
  deadline: string; // YYYY-MM-DD
  prize: string;
  regions: string[];
  industries: string[];
  eligibility: string[];
  type: string | null;
  status: typeof OpportunityStatus.WAITING_APPROVAL;
}

type CardItem = Opportunity | OpportunityDraft;

function isDraft(item: CardItem): item is OpportunityDraft {
  return '__draft' in item;
}

// ── Opportunity Card ──────────────────────────────────────────────────────────

interface OpportunityCardLabels {
  clickToEdit: string;
  newOpportunity: string;
  save: string;
  saving: string;
  cancel: string;
  deadlineLabel: string;
  noDeadline: string;
  prizeLabel: string;
  prizePlaceholder: string;
  nameError: string;
  approve: string;
  approving: string;
  noDescription: string;
  delete: string;
  collapse: string;
  regionsLabel: string;
  regionsPlaceholder: string;
  industriesLabel: string;
  industriesPlaceholder: string;
  eligibilityLabel: string;
  eligibilityPlaceholder: string;
  typeLabel: string;
  typePlaceholder: string;
  status: Record<OpportunityStatus, string>;
}

interface OpportunityCardProps {
  item: CardItem;
  expanded: boolean;
  onToggle: () => void;
  onSave: (item: CardItem, payload: UpsertOpportunityPayload) => Promise<void>;
  onDiscard: (item: CardItem) => void;
  onApprove: (id: string) => Promise<void>;
  onDelete: (item: CardItem) => void;
  labels: OpportunityCardLabels;
  saving: boolean;
  approving: boolean;
  typeOptions: MultiSelectOption[];
  industryOptions: FieldOption[];
  eligibilityOptions: MultiSelectOption[];
  regionOptions: SelectOption[];
}

function OpportunityCard({
  item,
  expanded,
  onToggle,
  onSave,
  onDiscard,
  onApprove,
  onDelete,
  labels,
  saving,
  approving,
  typeOptions,
  industryOptions,
  eligibilityOptions,
  regionOptions,
}: OpportunityCardProps) {
  const [name, setName] = useState(isDraft(item) ? item.name : item.name);
  const [description, setDescription] = useState(
    isDraft(item) ? item.description : (item.description ?? '')
  );
  const [link, setLink] = useState(isDraft(item) ? item.link : (item.link ?? ''));
  const [deadline, setDeadline] = useState(
    isDraft(item) ? item.deadline : toInputDate(item.deadline)
  );
  const [prize, setPrize] = useState(isDraft(item) ? item.prize : (item.prize ?? ''));
  const [regions, setRegions] = useState<string[]>(
    isDraft(item) ? item.regions : (item.regions?.map((r) => r.code) ?? [])
  );
  const [industries, setIndustries] = useState<string[]>(
    isDraft(item) ? item.industries : (item.industries ?? [])
  );
  const [eligibility, setEligibility] = useState<string[]>(
    isDraft(item) ? item.eligibility : (item.eligibility?.map((e) => e.value) ?? [])
  );
  const [type, setType] = useState<string | null>(
    isDraft(item) ? item.type : (item.type?.value ?? null)
  );
  const [nameError, setNameError] = useState('');

  // Sync local state when item prop changes (e.g. after server refetch).
  // Storing prev prop in state is the React-recommended alternative to useEffect for derived state.
  const [prevItem, setPrevItem] = useState(item);
  if (prevItem !== item) {
    setPrevItem(item);
    setName(item.name);
    setDescription(isDraft(item) ? item.description : (item.description ?? ''));
    setLink(isDraft(item) ? item.link : (item.link ?? ''));
    setDeadline(isDraft(item) ? item.deadline : toInputDate(item.deadline));
    setPrize(isDraft(item) ? item.prize : (item.prize ?? ''));
    setRegions(isDraft(item) ? item.regions : (item.regions?.map((r) => r.code) ?? []));
    setIndustries(isDraft(item) ? item.industries : (item.industries ?? []));
    setEligibility(
      isDraft(item) ? item.eligibility : (item.eligibility?.map((e) => e.value) ?? [])
    );
    setType(isDraft(item) ? item.type : (item.type?.value ?? null));
  }

  const handleSave = async () => {
    if (!name.trim()) {
      setNameError(labels.nameError);
      return;
    }
    setNameError('');

    const payload: UpsertOpportunityPayload = {
      name: name.trim(),
      ...(description.trim() && { description: description.trim() }),
      ...(link.trim() && { link: link.trim() }),
      ...(deadline && { deadline: toISODate(deadline) }),
      ...(prize.trim() && { prize: prize.trim() }),
      ...(regions.length > 0 && { regions }),
      ...(industries.length > 0 && { industries }),
      ...(eligibility.length > 0 && { eligibility }),
      ...(type && { type }),
    };

    await onSave(item, payload);
  };

  const color = STATUS_COLORS[item.status as OpportunityStatus];
  const deadlineDisplay = formatDate(
    isDraft(item) ? (deadline ? toISODate(deadline) : null) : item.deadline,
    labels.noDeadline
  );

  if (expanded) {
    return (
      <div className="border border-base-200 rounded-lg">
        <div className="px-4 py-3 space-y-3">
          {/* Header: status dot + status label + delete + collapse chevron */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
              <span className="text-sm text-[#EAEAEA]">
                {labels.status[item.status as OpportunityStatus]}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {/* Delete button */}
              <button
                type="button"
                onClick={() => onDelete(item)}
                className="btn btn-ghost btn-sm"
                aria-label={labels.delete}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
              {/* Collapse chevron */}
              <button
                type="button"
                onClick={onToggle}
                className="btn btn-ghost btn-sm"
                aria-label={labels.collapse}
              >
                <svg
                  className="w-4 h-4 text-base-content/40 rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Name — inline edit */}
          <InlineField
            value={name}
            placeholder={labels.newOpportunity}
            onChange={(v) => {
              setNameError('');
              setName(v);
            }}
            className="font-semibold"
          />
          {nameError && <p className="text-xs text-error px-1">{nameError}</p>}

          {/* Description — textarea inline */}
          <div className="flex items-start gap-2 py-0.5">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={labels.clickToEdit}
              className="textarea textarea-bordered textarea-sm w-full resize-none text-sm"
              rows={3}
            />
          </div>

          {/* Link — inline edit url */}
          <InlineField
            value={link}
            placeholder={labels.clickToEdit}
            onChange={setLink}
            type="url"
          />

          {/* Deadline — inline edit date */}
          <InlineField
            value={deadline}
            displayValue={deadline ? formatDate(toISODate(deadline), labels.noDeadline) : ''}
            placeholder={labels.clickToEdit}
            onChange={setDeadline}
            type="date"
            label={labels.deadlineLabel}
          />

          {/* Prize — $ prefix shown in display mode only, raw value stored */}
          <InlineField
            value={prize}
            displayValue={prize ? `$ ${prize}` : ''}
            placeholder={labels.prizePlaceholder}
            onChange={setPrize}
            label={labels.prizeLabel}
          />

          {/* Regions — sourced from auth-required /options/regions */}
          <div className="flex flex-col gap-1 px-1">
            <span className="text-sm text-[#EAEAEA]">{labels.regionsLabel}</span>
            <CountryMultiSelect
              options={regionOptions}
              value={regions}
              onChange={setRegions}
              placeholder={labels.regionsPlaceholder}
            />
          </div>

          {/* Industries — multi-select fetched from backend */}
          <div className="flex flex-col gap-1 px-1">
            <span className="text-sm text-[#EAEAEA]">{labels.industriesLabel}</span>
            <MultiSelect
              options={industryOptions}
              value={industries}
              onChange={setIndustries}
              placeholder={labels.industriesPlaceholder}
              variant="light"
            />
          </div>

          {/* Eligibility — multi-select fetched from backend (4 grouped categories, 29 tags) */}
          <div className="flex flex-col gap-1 px-1">
            <span className="text-sm text-[#EAEAEA]">{labels.eligibilityLabel}</span>
            <MultiSelect
              options={eligibilityOptions}
              value={eligibility}
              onChange={setEligibility}
              placeholder={labels.eligibilityPlaceholder}
              variant="light"
            />
          </div>

          {/* Type — grouped select fetched from backend */}
          <div className="flex flex-col gap-1 px-1">
            <span className="text-sm text-[#EAEAEA]">{labels.typeLabel}</span>
            <MultiSelect
              options={typeOptions}
              value={type ? [type] : []}
              onChange={(v) => setType(v[0] ?? null)}
              placeholder={labels.typePlaceholder}
              variant="light"
              multiple={false}
              clearLabel={labels.typePlaceholder}
            />
          </div>

          {/* Action row: approve (if pending) + cancel/save */}
          <div className="flex items-center justify-between pt-2 gap-2">
            <div>
              {/* Approve button only for existing pending opportunities */}
              {!isDraft(item) && item.status === OpportunityStatus.WAITING_APPROVAL && (
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={() => onApprove(item.id)}
                  disabled={approving}
                >
                  {approving ? (
                    <span className="loading loading-spinner loading-xs" />
                  ) : (
                    labels.approve
                  )}
                </button>
              )}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => onDiscard(item)}
                disabled={saving}
              >
                {labels.cancel}
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="loading loading-spinner loading-xs" />
                    {labels.saving}
                  </>
                ) : (
                  labels.save
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Collapsed
  return (
    <div
      className="border border-base-200 rounded-lg flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-base-200/30 transition-colors"
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onToggle()}
    >
      <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color }} />
      <span className="font-medium flex-1 truncate">{name || labels.newOpportunity}</span>
      <span className="text-sm text-base-content/60 shrink-0">
        {labels.deadlineLabel} {deadlineDisplay}
      </span>
      <svg
        className="w-4 h-4 text-base-content/40 shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );
}

// ── Opportunity Group ─────────────────────────────────────────────────────────

interface OpportunityGroupProps {
  title: string;
  items: CardItem[];
  expandedId: string | null;
  onToggle: (id: string) => void;
  onSave: (item: CardItem, payload: UpsertOpportunityPayload) => Promise<void>;
  onDiscard: (item: CardItem) => void;
  onApprove: (id: string) => Promise<void>;
  onDelete: (item: CardItem) => void;
  cardLabels: OpportunityCardLabels;
  savingId: string | null;
  approvingId: string | null;
  typeOptions: MultiSelectOption[];
  industryOptions: FieldOption[];
  eligibilityOptions: MultiSelectOption[];
  regionOptions: SelectOption[];
}

function OpportunityGroup({
  title,
  items,
  expandedId,
  onToggle,
  onSave,
  onDiscard,
  onApprove,
  onDelete,
  cardLabels,
  savingId,
  approvingId,
  typeOptions,
  industryOptions,
  eligibilityOptions,
  regionOptions,
}: OpportunityGroupProps) {
  return (
    <div className="border border-base-200 rounded-xl bg-[#1E1E1E] p-4">
      {/* Group header */}
      <div className="mb-3">
        <h2 className="font-semibold text-base">{title}</h2>
      </div>

      {/* Cards */}
      <div className="space-y-2">
        {items.map((item) => (
          <OpportunityCard
            key={item.id}
            item={item}
            expanded={expandedId === item.id}
            onToggle={() => onToggle(item.id)}
            onSave={onSave}
            onDiscard={onDiscard}
            onApprove={onApprove}
            onDelete={onDelete}
            labels={cardLabels}
            saving={savingId === item.id}
            approving={approvingId === item.id}
            typeOptions={typeOptions}
            industryOptions={industryOptions}
            eligibilityOptions={eligibilityOptions}
            regionOptions={regionOptions}
          />
        ))}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

let draftCounter = 0;

export function OpportunitiesPage() {
  const { t, i18n } = useTranslation('admin');
  const lang = i18n.language?.slice(0, 2) ?? 'es';

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedSearch(searchInput);
  const [statusFilter, setStatusFilter] = useState<OpportunityStatus | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [regionFilters, setRegionFilters] = useState<string[]>([]);
  const [industryFilters, setIndustryFilters] = useState<string[]>([]);
  const [eligibilityFilters, setEligibilityFilters] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showFilter, setShowFilter] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const [activeSortIndex, setActiveSortIndex] = useState(0);
  const [drafts, setDrafts] = useState<OpportunityDraft[]>([]);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CardItem | null>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);

  const sortOptions = useMemo(
    () => [
      {
        label: t('opportunities.sort.deadlineAsc'),
        sortBy: 'deadline' as const,
        sortOrder: 'asc' as const,
      },
      {
        label: t('opportunities.sort.deadlineDesc'),
        sortBy: 'deadline' as const,
        sortOrder: 'desc' as const,
      },
      {
        label: t('opportunities.sort.newest'),
        sortBy: 'createdAt' as const,
        sortOrder: 'desc' as const,
      },
    ],
    [t]
  );

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilter(false);
      }
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setShowSort(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toastMessage) return;
    const timer = setTimeout(() => setToastMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  const activeSort = sortOptions[activeSortIndex];

  const queryParams: OpportunityQueryParams = {
    search: search || undefined,
    status: statusFilter,
    type: typeFilter,
    regions: regionFilters.length ? regionFilters : undefined,
    industries: industryFilters.length ? industryFilters : undefined,
    eligibility: eligibilityFilters.length ? eligibilityFilters : undefined,
    sortBy: activeSort.sortBy,
    sortOrder: activeSort.sortOrder,
    page,
    limit: PAGE_SIZE,
  };

  const { opportunities, meta, loading, error, refetch } = useOpportunities(queryParams);

  useEffect(() => {
    if (error) captureException(error);
  }, [error]);

  const { approve } = useApproveOpportunity();
  const { upsert } = useUpsertOpportunity();
  const { update } = useUpdateOpportunity();
  const { deleteById, loading: deleting } = useDeleteOpportunity();

  const { groups: filterTypeGroups } = useOpportunityTypes(lang);
  const filterTypeOptions = useMemo(
    () =>
      filterTypeGroups.flatMap((g) =>
        g.options.map((o) => ({ value: o.value, label: o.label, group: g.group }))
      ),
    [filterTypeGroups]
  );
  const { options: industryOptions } = useFieldOptions('industry', lang);
  const { groups: filterEligibilityGroups } = useEligibility(lang);
  const filterEligibilityOptions = useMemo(
    () =>
      filterEligibilityGroups.flatMap((g) =>
        g.options.map((o) => ({ value: o.value, label: o.label, group: g.group }))
      ),
    [filterEligibilityGroups]
  );
  const { regions: regionOptions } = useRegions();

  const statusLabels: Record<OpportunityStatus, string> = {
    [OpportunityStatus.WAITING_APPROVAL]: t('opportunities.status.WAITING_APPROVAL'),
    [OpportunityStatus.APPROVED]: t('opportunities.status.APPROVED'),
  };

  const cardLabels: OpportunityCardLabels = {
    clickToEdit: t('opportunities.clickToEdit'),
    newOpportunity: t('opportunities.newOpportunity'),
    save: t('opportunities.addModal.save'),
    saving: t('opportunities.addModal.saving'),
    cancel: t('opportunities.addModal.cancel'),
    deadlineLabel: t('opportunities.table.deadline'),
    noDeadline: t('opportunities.noDeadline'),
    prizeLabel: t('opportunities.addModal.prize'),
    prizePlaceholder: t('opportunities.addModal.prizePlaceholder'),
    nameError: t('opportunities.addModal.nameError'),
    approve: t('opportunities.approve'),
    approving: t('opportunities.approving'),
    noDescription: t('opportunities.noDescription'),
    delete: t('opportunities.delete'),
    collapse: t('opportunities.collapse'),
    regionsLabel: t('opportunities.addModal.regions'),
    regionsPlaceholder: t('opportunities.addModal.regionsPlaceholder'),
    industriesLabel: t('opportunities.addModal.industries'),
    industriesPlaceholder: t('opportunities.addModal.industriesPlaceholder'),
    eligibilityLabel: t('opportunities.addModal.eligibility'),
    eligibilityPlaceholder: t('opportunities.addModal.eligibilityPlaceholder'),
    typeLabel: t('opportunities.addModal.type'),
    typePlaceholder: t('opportunities.addModal.typePlaceholder'),
    status: statusLabels,
  };

  const toggleStatusFilter = (status: OpportunityStatus) => {
    setStatusFilter((prev) => (prev === status ? undefined : status));
    setPage(1);
  };

  const handleToggle = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleAddDraft = () => {
    const draft: OpportunityDraft = {
      __draft: true,
      id: `__draft__${++draftCounter}`,
      name: '',
      description: '',
      link: '',
      deadline: '',
      prize: '',
      regions: [],
      industries: [],
      eligibility: [],
      type: null,
      status: OpportunityStatus.WAITING_APPROVAL,
    };
    setDrafts((prev) => [draft, ...prev]);
    setExpandedId(draft.id);
  };

  const handleDiscard = (item: CardItem) => {
    if (isDraft(item)) {
      setDrafts((prev) => prev.filter((d) => d.id !== item.id));
    }
    setExpandedId(null);
  };

  const handleSave = async (item: CardItem, payload: UpsertOpportunityPayload) => {
    setSavingId(item.id);
    const { data, error } = isDraft(item) ? await upsert(payload) : await update(item.id, payload);
    setSavingId(null);

    if (data) {
      setToastMessage(t('opportunities.addModal.success'));
      if (isDraft(item)) {
        setDrafts((prev) => prev.filter((d) => d.id !== item.id));
      }
      setExpandedId(null);
      refetch();
    } else {
      captureException(error);
      setToastMessage(getErrorMessage(error, t));
    }
  };

  const handleApprove = async (id: string) => {
    setApprovingId(id);
    const { data, error } = await approve(id);
    setApprovingId(null);

    if (data) {
      setToastMessage(t('opportunities.approveSuccess'));
      refetch();
    } else {
      captureException(error);
      setToastMessage(getErrorMessage(error, t));
    }
  };

  // Drafts are discarded immediately; saved items require confirmation via modal
  const handleDeleteRequest = (item: CardItem) => {
    if (isDraft(item)) {
      setDrafts((prev) => prev.filter((d) => d.id !== item.id));
      setExpandedId(null);
      return;
    }
    setDeleteTarget(item);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || isDraft(deleteTarget)) return;
    const { data, error } = await deleteById(deleteTarget.id);
    setDeleteTarget(null);
    if (data) {
      setToastMessage(t('opportunities.deleteSuccess'));
      setExpandedId(null);
      refetch();
    } else {
      captureException(error);
      setToastMessage(getErrorMessage(error, t));
    }
  };

  // Separate items by status for grouping
  const pendingItems: CardItem[] = [
    ...drafts,
    ...opportunities.filter((o) => o.status === OpportunityStatus.WAITING_APPROVAL),
  ];
  const approvedItems: CardItem[] = opportunities.filter(
    (o) => o.status === OpportunityStatus.APPROVED
  );

  return (
    <div className="p-6 bg-[#1E1E1E] rounded-lg">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <h1 className="text-2xl font-bold mb-6">{t('opportunities.title')}</h1>

        {/* Toolbar: search + sort placeholder + filter + add */}
        <div className="flex items-center gap-2 mb-4">
          <div className="flex-1 relative">
            <svg
              className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
              placeholder={t('opportunities.search')}
              className="input input-bordered w-full pl-10"
            />
          </div>

          {/* Sort */}
          <div ref={sortRef} className="relative">
            <button
              type="button"
              onClick={() => {
                setShowSort(!showSort);
                setShowFilter(false);
              }}
              className={`btn btn-ghost btn-square ${activeSortIndex !== 0 ? 'text-primary' : ''}`}
              aria-label={t('opportunities.sort.label')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                />
              </svg>
            </button>
            {showSort && (
              <div className="absolute right-0 top-full mt-1 bg-base-100 border border-base-200 rounded-lg shadow-lg z-20 min-w-[200px]">
                {sortOptions.map((opt, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-base-200 ${idx === activeSortIndex ? 'font-bold text-primary' : ''}`}
                    onClick={() => {
                      setActiveSortIndex(idx);
                      setPage(1);
                      setShowSort(false);
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Filter */}
          <div ref={filterRef} className="relative">
            <button
              type="button"
              onClick={() => {
                setShowFilter(!showFilter);
                setShowSort(false);
              }}
              aria-label={t('opportunities.filter')}
              aria-expanded={showFilter}
              className={`btn btn-ghost btn-square ${statusFilter || typeFilter || regionFilters.length || industryFilters.length || eligibilityFilters.length ? 'text-primary' : ''}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
            </button>
            {showFilter && (
              <div className="absolute right-0 top-full mt-1 bg-base-100 border border-base-200 rounded-lg shadow-lg z-20 w-72 p-3 space-y-3">
                {/* Status */}
                <div>
                  <p className="text-xs font-semibold text-base-content/50 uppercase tracking-wider mb-1.5">
                    {t('opportunities.filters.status')}
                  </p>
                  {Object.values(OpportunityStatus).map((status) => (
                    <label
                      key={status}
                      className="flex items-center gap-2 px-1 py-1 cursor-pointer hover:bg-base-200 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={statusFilter === status}
                        onChange={() => toggleStatusFilter(status)}
                        className="checkbox checkbox-xs checkbox-primary"
                      />
                      <span className="text-sm">{statusLabels[status]}</span>
                    </label>
                  ))}
                </div>

                {/* Type */}
                <div>
                  <p className="text-xs font-semibold text-base-content/50 uppercase tracking-wider mb-1.5">
                    {t('opportunities.filters.type')}
                  </p>
                  <MultiSelect
                    options={filterTypeOptions}
                    value={typeFilter ? [typeFilter] : []}
                    onChange={(v) => {
                      setTypeFilter(v[0]);
                      setPage(1);
                    }}
                    placeholder={t('opportunities.filters.type')}
                    variant="light"
                    multiple={false}
                    clearLabel={t('opportunities.filters.any')}
                  />
                </div>

                {/* Country */}
                <div>
                  <p className="text-xs font-semibold text-base-content/50 uppercase tracking-wider mb-1.5">
                    {t('opportunities.filters.region')}
                  </p>
                  <CountryMultiSelect
                    options={regionOptions}
                    value={regionFilters}
                    onChange={(v) => {
                      setRegionFilters(v);
                      setPage(1);
                    }}
                    placeholder={t('opportunities.filters.region')}
                  />
                </div>

                {/* Industry */}
                <div>
                  <p className="text-xs font-semibold text-base-content/50 uppercase tracking-wider mb-1.5">
                    {t('opportunities.filters.industry')}
                  </p>
                  <MultiSelect
                    options={industryOptions}
                    value={industryFilters}
                    onChange={(v) => {
                      setIndustryFilters(v);
                      setPage(1);
                    }}
                    placeholder={t('opportunities.filters.industry')}
                    variant="light"
                  />
                </div>

                {/* Eligibility */}
                <div>
                  <p className="text-xs font-semibold text-base-content/50 uppercase tracking-wider mb-1.5">
                    {t('opportunities.filters.eligibility')}
                  </p>
                  <MultiSelect
                    options={filterEligibilityOptions}
                    value={eligibilityFilters}
                    onChange={(v) => {
                      setEligibilityFilters(v);
                      setPage(1);
                    }}
                    placeholder={t('opportunities.filters.eligibility')}
                    variant="light"
                  />
                </div>

                {/* Clear all */}
                {(statusFilter ||
                  typeFilter ||
                  regionFilters.length ||
                  industryFilters.length > 0 ||
                  eligibilityFilters.length > 0) && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs w-full"
                    onClick={() => {
                      setStatusFilter(undefined);
                      setTypeFilter(undefined);
                      setRegionFilters([]);
                      setIndustryFilters([]);
                      setEligibilityFilters([]);
                      setPage(1);
                    }}
                  >
                    {t('opportunities.filters.clearAll')}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Add button — in toolbar, next to filters */}
          <button
            type="button"
            className="btn btn-primary gap-1.5 shrink-0"
            onClick={handleAddDraft}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            {t('opportunities.add')}
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center h-64">
            <span className="loading loading-spinner loading-lg" />
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div role="alert" className="alert alert-error mb-4">
            <span>{getErrorMessage(error, t)}</span>
          </div>
        )}

        {/* Groups */}
        {!loading && (
          <div className="space-y-4">
            {/* Pending group — always visible (drafts may exist even when no server items) */}
            {(pendingItems.length > 0 || !error) && (
              <OpportunityGroup
                title={t('opportunities.groups.pending')}
                items={pendingItems}
                expandedId={expandedId}
                onToggle={handleToggle}
                onSave={handleSave}
                onDiscard={handleDiscard}
                onApprove={handleApprove}
                onDelete={handleDeleteRequest}
                cardLabels={cardLabels}
                savingId={savingId}
                approvingId={approvingId}
                typeOptions={filterTypeOptions}
                industryOptions={industryOptions}
                eligibilityOptions={filterEligibilityOptions}
                regionOptions={regionOptions}
              />
            )}

            {/* Approved group */}
            {approvedItems.length > 0 && (
              <OpportunityGroup
                title={t('opportunities.groups.approved')}
                items={approvedItems}
                expandedId={expandedId}
                onToggle={handleToggle}
                onSave={handleSave}
                onDiscard={handleDiscard}
                onApprove={handleApprove}
                onDelete={handleDeleteRequest}
                cardLabels={cardLabels}
                savingId={savingId}
                approvingId={approvingId}
                typeOptions={filterTypeOptions}
                industryOptions={industryOptions}
                eligibilityOptions={filterEligibilityOptions}
                regionOptions={regionOptions}
              />
            )}

            {/* Empty state — only when no items at all and no pending drafts */}
            {!error && pendingItems.length === 0 && approvedItems.length === 0 && (
              <EmptyState
                description={t('opportunities.emptyDescription')}
                illustration={
                  <img src={opportunitiesEmptyImage} alt="" className="w-48 h-48 mx-auto mb-4" />
                }
              />
            )}

            {/* Pagination */}
            <Pagination
              currentPage={meta.page}
              totalPages={meta.totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Delete confirmation modal — only for saved opportunities, not drafts */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        message={t('opportunities.deleteConfirm.message')}
        confirmLabel={t('opportunities.deleteConfirm.confirm')}
        variant="danger"
        isLoading={deleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Toast */}
      {toastMessage && (
        <div className="toast toast-top toast-end z-50">
          <div className="alert alert-success shadow-lg">
            <span>{toastMessage}</span>
          </div>
        </div>
      )}
    </div>
  );
}
